/**
 * Creates the label for the current page selection.
 * @param {Number} [currentPage=page] - The page number to use as the current page.
 * @return {String}
 */
const getCurrentItemsCaption = _ref => {
  let {
    page: currentPage,
    componentStrings,
    customStrings,
    itemsPerPage,
    totalItems
  } = _ref;
  const {
    to
  } = componentStrings;
  const {
    currentItemsTextInTrigger
  } = customStrings || {};
  const page = currentPage - 1;
  const startOfRange = page * itemsPerPage + 1;
  let endOfRange = page * itemsPerPage + itemsPerPage;

  if (endOfRange > totalItems) {
    endOfRange = totalItems;
  } // Show customized application Paginator button text.


  if (currentItemsTextInTrigger) {
    return currentItemsTextInTrigger(startOfRange, endOfRange, itemsPerPage);
  } // Show default Paginator button text.


  return `${startOfRange} ${to} ${endOfRange}`;
};
/**
 * Obtains page attribute value.
 * `page` is 1-index based. Possible pageOptions are maintained in array which is 0-index based.
 * So for internal array operations, subtracting 1 from page number.
 * @return {Number}
 */


const getCurrentPage = page => page - 1;
/**
 * Returns the label for the total items.
 * @return {String}
 */


const getTotalItemsLabel = _ref2 => {
  let {
    componentStrings,
    totalItems
  } = _ref2;
  const {
    of,
    itemsInTrigger
  } = componentStrings;
  return `${totalItems > 0 ? of : ""} ${totalItems} ${itemsInTrigger}`;
};
/**
 * Calculates `itemsPerPage` for current page.
 * @param {Number} currentPage - The page number to use as the current page.
 * @return {Number} - The number of items on the current page.
 */


const getItemsPerPage = _ref3 => {
  let {
    page: currentPage,
    itemsPerPage,
    totalItems
  } = _ref3;
  const lastPage = Math.ceil(totalItems / itemsPerPage);

  if (totalItems % itemsPerPage === 0 || currentPage !== lastPage) {
    return itemsPerPage;
  } else {
    /**
     * If `totalItems` is not divisible by `itemsPerPage`, last page has
     * less items than `itemsPerPage`, so return exact number of remaining items.
     */
    return totalItems % itemsPerPage;
  }
};
/**
 * Obtains the selected index on items per page list to be mark as selected.
 * @return {Number}
 */


const getSelectedItemsPerPageIndex = _ref4 => {
  let {
    itemsPerPage,
    pageSizes
  } = _ref4;
  return pageSizes.indexOf(itemsPerPage);
};
/**
 * Obtains the selected index on view items list to be mark as selected.
 * @return {Number}
 */


const getSelectedPageItemIndex = _ref5 => {
  let {
    page,
    pagesPerMenu,
    viewItemsSet
  } = _ref5;

  /**
   * Determines the first page number in current View Items set
   * E.g. Assuming pagesPerMenu is 5 and View Items set is 2,
   * then first page number in view items is 10. Since it is 0-index based, the actual page number is 11.
   */
  const firstPageNumberInCurrentViewItemSet = viewItemsSet * pagesPerMenu;
  /**
   * Determines the item to be shown as selected in View Items Menu.
   * E.g. Assuming viewItemOptions [41-50, 51-60], current page is 41 and first page number is 41
   * then the index of item to be selected in menu is 0.
   */

  const selectedPageItemIndex = getCurrentPage(page) - firstPageNumberInCurrentViewItemSet;
  return `${selectedPageItemIndex}`;
};
/**
 * Returns the ending portion of the Paginator trigger button label.
 * @return {String}
 */


const getTotalItemsLabelInTrigger = _ref6 => {
  let {
    componentStrings,
    customStrings,
    totalItems
  } = _ref6;
  const {
    of
  } = componentStrings;
  const {
    itemsInTrigger
  } = customStrings;
  const totalItemsInTrigger = itemsInTrigger ? `${totalItems} ${itemsInTrigger}` : totalItems;
  return `${totalItems > 0 ? of : ""} ${totalItemsInTrigger}`;
};
/**
 * Calculates the total number of pages based on total records and items per page.
 * @return {number}
 */


const getTotalPages = _ref7 => {
  let {
    totalItems,
    itemsPerPage
  } = _ref7;
  return Math.ceil(totalItems / itemsPerPage);
};
/**
 * Creates the array of labels for the pages to be shown in the ViewItems Menu.
 * @return {Array}
 */


const getViewItemsOptions = _ref8 => {
  let {
    itemsPerPage,
    viewItemsSet,
    pagesPerMenu,
    totalItems
  } = _ref8;
  const pageSizes = [];
  let from, to, i;
  const initialPage = viewItemsSet * pagesPerMenu;

  for (i = 0; i < pagesPerMenu; i++) {
    from = (initialPage + i) * itemsPerPage + 1;
    to = from + itemsPerPage - 1;

    if (from <= totalItems) {
      if (to > totalItems) {
        to = totalItems;
      }

      pageSizes[i] = {
        fromVal: from,
        toVal: to
      };
    } else {
      break;
    }
  }

  return pageSizes;
};

const getViewItemsSet = _ref9 => {
  let {
    page,
    pagesPerMenu
  } = _ref9;
  // return Math.ceil(page / pagesPerMenu) - 1;
  return Math.ceil(page / pagesPerMenu) - 1;
};
/**
 * Calculates if the viewItems menu section should have or not a Nav bar.
 * @return {Boolean}
 */


const hasMenuPages = _ref10 => {
  let {
    totalItems,
    itemsPerPage,
    pagesPerMenu
  } = _ref10;
  return totalItems / itemsPerPage > pagesPerMenu;
};
/**
 * Toggles the Balloon open/close
 */


const toggleBalloon = setOpen => {
  setOpen(!open);
};
/**
 * Handles Paginator clicks.
 * - Opens the Paginator.
 */


const triggerClickHandler = _ref11 => {
  let {
    open,
    setOpen
  } = _ref11;
  setOpen(!open);
};

export { getCurrentItemsCaption, getItemsPerPage, getSelectedItemsPerPageIndex, getSelectedPageItemIndex, getTotalPages, getTotalItemsLabel, getTotalItemsLabelInTrigger, getViewItemsOptions, getViewItemsSet, hasMenuPages, toggleBalloon, triggerClickHandler };